import React, { useState, useEffect } from 'react';
import { getConsentBranding } from '../../utils/consentBranding';
import {
    addLead,
    addCustomer,
    getOrganizations,
    getTeamsByOrganization,
    getUserSubTeamInfo,
    sendEmail,
    getLeadTemplates,
    saveLeadTemplate,
    deleteLeadTemplate
} from '../../api';
import { useNavigate, useLocation } from 'react-router-dom';
import SignatureField from '../../components/SignatureField';
import OptInToggle from '../../components/common/OptInToggle';
import LeadTemplateModal from '../../components/common/LeadTemplateModal';
import PvFormStep from '../../components/forms/PvFormStep';
import WpFormStep from '../../components/forms/WpFormStep';
import DashboardNav from '../../components/dashboard/DashboardNav';
import {
    hasQualifyingPvConsumption,
    isPvConsumptionStepComplete,
    isPvConsumptionDisqualified,
    getPvConsumptionSubmitValues,
    isYearlyConsumptionOver2700Valid
} from '../../utils/pvConsumptionQualification';

function PvAndWPForm({ user }) {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get('customerId');

    // Schritt-Management: 1=PV, 2=WP, 3=Kundendaten
    const [step, setStep] = useState(1);
    const [organizationName, setOrganizationName] = useState('');
    const [teamName, setTeamName] = useState('');

    // PV-Daten (aus LeadForm.js)
    const [pvData, setPvData] = useState({
        isOwner: null,
        buildingType: '',
        roofOrientation: '',
        roofAreaOver20: null,
        roofAreaSize: '',
        hasAlternativeMounting: null,
        alternativeMountingArea: '',
        hasMonumentProtection: null,
        consumptionOver2700: null,
        consumptionInRangeWith3Months: null,
        hasRealizationTimeframe: null,
        hasFutureEnergyConsumers: null,
        yearlyConsumption: '',
    });

    // WP-Daten (aus WpForm.js)
    const [wpData, setWpData] = useState({
        isOwner: null,
        buildingType: '',
        livingAreaSize: '',
        peopleInHouse: '',
        buildYear: '',
        hasGasOrOil: null,
        heatingOlderThan2Years: null,
        currentHeatingSystem: '',
        hasSolarThermal: null,
        solarThermalRemoved: null,
        hasMonumentProtection: null,
    });

    // Kundendaten (gemeinsam)
    const [formData, setFormData] = useState({
        produkt: 'PV+WP', // Kombiniertes Produkt
        signature: '',
        dienstleister: user?.full_name || '',
        projekt: '', // Wird automatisch basierend auf Team/Sub-Team gesetzt
        anrede: '',
        name: '',
        nachname: '',
        email: '',
        handy: '',
        strasse: '',
        hausnummer: '',
        postleitzahl: '',
        stadt: '',
        projekt: '', // Projekt wird automatisch ermittelt
    });

    // Fortschritt der progressiven Offenlegung für PV
    const [pvProgression, setPvProgression] = useState(1);
    // Fortschritt der progressiven Offenlegung für WP
    const [wpProgression, setWpProgression] = useState(1);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userProjectName, setUserProjectName] = useState('');
    const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
    const [pendingSubmit, setPendingSubmit] = useState(null);

    // Opt-In Toggle State
    const [optInMode, setOptInMode] = useState('signature');

    // Team/Projekt-Informationen laden
    useEffect(() => {
        const loadTeamName = async () => {
            if (user && user.id && user.organization_id && user.team_id) {
                try {
                    // Sub-Team-Informationen laden
                    const subTeamResponse = await getUserSubTeamInfo(user.id);
                    let projectName = '';
                    
                    if (subTeamResponse.success && subTeamResponse.userInfo) {
                        const userInfo = subTeamResponse.userInfo;
                        
                        // Wenn Benutzer in einem Sub-Team ist, zeige "Team-Name (Sub-Team-Name)"
                        if (userInfo.sub_team_name) {
                            projectName = `${userInfo.team_name} (${userInfo.sub_team_name})`;
                        } else {
                            // Wenn nur Team, zeige nur Team-Name
                            projectName = userInfo.team_name;
                        }
                    } else {
                        // Fallback: Team-Informationen laden
                        const response = await getTeamsByOrganization(user.id, user.organization_id);
                        if (response.success) {
                            const userTeam = response.teams.find(team => team.id === user.team_id);
                            if (userTeam) {
                                projectName = userTeam.name;
                            } else {
                                projectName = 'Mein Team';
                            }
                        } else {
                            projectName = user.team?.name || 'Unbekanntes Team';
                        }
                    }
                    
                    setTeamName(projectName);
                    setFormData(prev => ({
                        ...prev,
                        projekt: projectName,
                        dienstleister: user.full_name || user.email || ''
                    }));
                } catch (err) {
                    console.error('Fehler beim Laden der Team/Sub-Team-Informationen:', err);
                    // Fallback bei Netzwerkfehler
                    setTeamName(user.team?.name || 'Mein Team');
                    setFormData(prev => ({
                        ...prev,
                        projekt: user.team?.name || 'Mein Team',
                        dienstleister: user.full_name || user.email || ''
                    }));
                }
            } else {
                // Fallback: Wenn keine Team-ID vorhanden, versuche Organization zu laden
                try {
                    const response = await getOrganizations();
                    if (response.success && response.organizations.length > 0) {
                        const orgName = response.organizations[0].name;
                        setOrganizationName(orgName);
                        setTeamName(orgName);
                        setFormData(prev => ({
                            ...prev,
                            projekt: orgName,
                            dienstleister: user.full_name || user.email || ''
                        }));
                    }
                } catch (err) {
                    console.error('Fehler beim Laden der Organisation:', err);
                    setFormData(prev => ({
                        ...prev,
                        projekt: 'Unbekanntes Team',
                        dienstleister: user.full_name || user.email || ''
                    }));
                }
            }
        };

        loadTeamName();
    }, [user]);

    // Vorlagen-Logik (aus LeadForm.js)
    const [templates, setTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templateError, setTemplateError] = useState('');
    const [templateSuccess, setTemplateSuccess] = useState('');
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

    // Projekt-Name automatisch laden
    useEffect(() => {
        const loadUserProject = async () => {
            if (!user?.id) return;
            
            try {
                // Versuche zuerst Sub-Team-Informationen zu laden
                const subTeamResponse = await getUserSubTeamInfo(user.id);
                if (subTeamResponse.success && subTeamResponse.userInfo) {
                    const userInfo = subTeamResponse.userInfo;
                    
                    // Wenn Benutzer in einem Sub-Team ist, zeige "Team-Name (Sub-Team-Name)"
                    if (userInfo.sub_team_name) {
                        const projectName = `${userInfo.team_name} (${userInfo.sub_team_name})`;
                        setUserProjectName(projectName);
                        setFormData(prev => ({ ...prev, projekt: projectName }));
                    } else {
                        // Wenn nur Team, zeige nur Team-Name
                        setUserProjectName(userInfo.team_name);
                        setFormData(prev => ({ ...prev, projekt: userInfo.team_name }));
                    }
                } else if (user.organization_id) {
                    // Fallback: Team-Informationen laden
                    const response = await getTeamsByOrganization(user.id, user.organization_id);
                    if (response.success) {
                        const userTeam = response.teams.find(team => team.id === user.team_id);
                        if (userTeam) {
                            setUserProjectName(userTeam.name);
                            setFormData(prev => ({ ...prev, projekt: userTeam.name }));
                        }
                    }
                }
            } catch (err) {
                console.log('Projekt-Info nicht verfügbar:', err);
            }
        };

        loadUserProject();
    }, [user]);

    // Schritt-Navigation
    const nextStep = () => {
        if (step === 1 && isPvStepValid()) {
            setStep(2);
        } else if (step === 2 && isWpStepValid()) {
            setStep(3);
        }
    };

    const prevStep = () => {
        if (step > 1) {
            setStep(step - 1);
        }
    };

    // Prüfen, ob PV Lead disqualifiziert ist
    const isPvDisqualified = () => {
        if (pvData.isOwner === false) return true;
        if (pvData.roofOrientation === 'Nord') return true;
        if (pvData.hasMonumentProtection === true) return true;

        // Dachflächen-Disqualifizierung
        const effectiveArea = getEffectiveRoofArea();
        if (pvData.roofAreaOver20 === false && pvData.hasAlternativeMounting === false) return true;
        if (effectiveArea > 0 && effectiveArea < 20) return true;

        if (isPvConsumptionDisqualified(pvData)) return true;

        return false;
    };

    // Prüfen, ob WP Lead disqualifiziert ist
    const isWpLivingAreaUnsupported = () => {
        return Number(wpData.livingAreaSize) > 250;
    };

    const isWpDisqualified = () => {
        if (wpData.isOwner === false) return true;
        if (isWpLivingAreaUnsupported()) return true;
        if (wpData.hasGasOrOil === false) return true;
        if (wpData.heatingOlderThan2Years === false) return true;
        if (wpData.hasMonumentProtection === true) return true;
        if (wpData.hasSolarThermal === true && wpData.solarThermalRemoved === false) return true;
        
        return false;
    };

    // Validierung für PV-Schritt (inklusive Disqualifizierung)
    const isPvStepValid = () => {
        const basicValidation = (
            pvData.isOwner === true &&
            pvData.buildingType !== '' &&
            pvData.roofOrientation !== '' &&
            pvData.roofAreaOver20 !== null &&
            (pvData.roofAreaOver20 === false || pvData.roofAreaSize.trim() !== '') &&
            pvData.hasMonumentProtection !== null &&
            isPvConsumptionStepComplete(pvData)
        );

        // Wenn alle Felder ausgefüllt sind, aber Lead disqualifiziert ist, dann nicht valid
        if (basicValidation && isPvDisqualified()) {
            return false;
        }

        return basicValidation;
    };

    // Validierung für WP-Schritt (inklusive Disqualifizierung)
    const isWpStepValid = () => {
        const basicValidation = (
            wpData.isOwner === true &&
            wpData.buildingType !== '' &&
            wpData.livingAreaSize.trim() !== '' &&
            wpData.peopleInHouse.trim() !== '' &&
            wpData.buildYear.trim() !== '' &&
            wpData.hasGasOrOil !== null &&
            wpData.heatingOlderThan2Years !== null &&
            wpData.currentHeatingSystem !== '' &&
            wpData.hasSolarThermal !== null &&
            (wpData.hasSolarThermal === false || 
             (wpData.hasSolarThermal === true && wpData.solarThermalRemoved !== null)) &&
            wpData.hasMonumentProtection !== null
        );

        // Wenn alle Felder ausgefüllt sind, aber Lead disqualifiziert ist, dann nicht valid
        if (basicValidation && isWpDisqualified()) {
            return false;
        }

        return basicValidation;
    };

    // Validierung für Kundendaten-Schritt
    const isCustomerStepValid = () => {
        return (
            formData.anrede.trim() !== '' &&
            formData.name.trim() !== '' &&
            formData.nachname.trim() !== '' &&
            formData.handy.trim() !== '' &&
            formData.strasse.trim() !== '' &&
            formData.hausnummer.trim() !== '' &&
            formData.postleitzahl.trim() !== '' &&
            formData.stadt.trim() !== ''
        );
    };

    // Handler für Formular-Änderungen
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (['anrede', 'name', 'nachname', 'email', 'handy', 'strasse', 'hausnummer', 'postleitzahl', 'stadt'].includes(name)) {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    // Handler für Opt-In Unterschrift
    const handleOptInSignatureChange = (signature) => {
        setFormData({
            ...formData,
            signature: signature
        });
    };
    
    // PV-Handler (aus LeadForm.js kopieren)
    const handlePvOwnershipChange = (value) => {
        setPvData(prev => ({
            ...prev,
            isOwner: value
        }));

        if (value === false) {
            setPvProgression(1); // Zurück zum Anfang bei Nein
        } else {
            setPvProgression(2); // Weiter bei Ja
        }
    };

    // Handler für PV-Datenänderungen mit Progression
    const handlePvDataChange = (updates) => {
        if (updates.consumptionOver2700 === true) {
            updates.consumptionInRangeWith3Months = null;
        }
        if (updates.consumptionOver2700 === false) {
            updates.hasRealizationTimeframe = null;
            updates.yearlyConsumption = '';
        }
        setPvData(prev => ({ ...prev, ...updates }));
        
        // Progression basierend auf den Änderungen aktualisieren
        const newData = { ...pvData, ...updates };
        
        if (updates.buildingType && pvProgression === 2) {
            setPvProgression(3);
        } else if (updates.roofOrientation && pvProgression === 3) {
            setPvProgression(4);
        } else if (updates.roofAreaOver20 !== undefined && pvProgression === 4) {
            if (updates.roofAreaOver20 === true) {
                setPvProgression(5);
            } else {
                setPvProgression(6);
            }
        } else if (updates.roofAreaSize && pvProgression === 5) {
            const area = Number(updates.roofAreaSize);
            if (area >= 20) {
                setPvProgression(8); // Direkt zu Denkmalschutz
            }
        } else if (updates.hasAlternativeMounting !== undefined && pvProgression === 6) {
            if (updates.hasAlternativeMounting === true) {
                setPvProgression(7);
            } else {
                setPvProgression(8); // Direkt zu Denkmalschutz
            }
        } else if (updates.alternativeMountingArea && pvProgression === 7) {
            const area = Number(updates.alternativeMountingArea);
            if (area >= 20) {
                setPvProgression(8); // Zu Denkmalschutz
            }
        } else if (updates.hasMonumentProtection !== undefined && pvProgression === 8) {
            setPvProgression(9);
        } else if (updates.yearlyConsumption !== undefined && pvProgression >= 9) {
            if (
                newData.consumptionOver2700 === true &&
                isYearlyConsumptionOver2700Valid(newData.yearlyConsumption)
            ) {
                setPvProgression(newData.hasRealizationTimeframe !== null ? 13 : 12);
            }
        } else if (updates.consumptionOver2700 === true && pvProgression >= 9) {
            setPvProgression(
                isYearlyConsumptionOver2700Valid(newData.yearlyConsumption) ? 12 : 9
            );
        } else if (updates.consumptionOver2700 === false && pvProgression >= 9) {
            setPvProgression(10);
        } else if (updates.consumptionInRangeWith3Months === true && pvProgression >= 10) {
            setPvProgression(11);
        } else if (updates.consumptionInRangeWith3Months === false && pvProgression >= 10) {
            setPvProgression(10);
        } else if (updates.hasRealizationTimeframe !== undefined && pvProgression >= 12) {
            setPvProgression(13);
        }
    };

    // WP-Handler (aus WpForm.js kopieren)
    const handleWpOwnershipChange = (value) => {
        setWpData(prev => ({
            ...prev,
            isOwner: value
        }));

        if (value === false) {
            setWpProgression(1); // Zurück zum Anfang bei Nein
        } else {
            setWpProgression(2); // Weiter bei Ja
        }
    };

    // Handler für WP-Datenänderungen mit Progression
    const handleWpDataChange = (updates) => {
        // Wenn hasSolarThermal geändert wird, solarThermalRemoved zurücksetzen wenn auf "Nein" gewechselt wird
        if (updates.hasSolarThermal === false && wpData.solarThermalRemoved !== null) {
            updates.solarThermalRemoved = null;
        }
        
        setWpData(prev => ({ ...prev, ...updates }));
        
        // Progression basierend auf den Änderungen aktualisieren
        if (updates.buildingType && wpProgression === 2) {
            setWpProgression(3);
        } else if (updates.livingAreaSize && wpProgression === 3) {
            setWpProgression(4);
        } else if (updates.peopleInHouse && wpProgression === 4) {
            setWpProgression(5);
        } else if (updates.buildYear && wpProgression === 5) {
            setWpProgression(6);
        } else if (updates.hasGasOrOil !== undefined && wpProgression === 6) {
            setWpProgression(7);
        } else if (updates.heatingOlderThan2Years !== undefined && wpProgression === 7) {
            setWpProgression(8);
        } else if (updates.currentHeatingSystem && wpProgression === 8) {
            setWpProgression(9);
        } else if (updates.hasSolarThermal !== undefined && wpProgression >= 9) {
            // >= 9 statt === 9, damit es auch funktioniert wenn man von Ja zurück zu Nein wechselt
            if (updates.hasSolarThermal === true) {
                setWpProgression(10);
            } else {
                setWpProgression(11);
            }
        } else if (updates.solarThermalRemoved !== undefined && wpProgression === 10) {
            setWpProgression(11);
        } else if (updates.hasMonumentProtection !== undefined && wpProgression === 11) {
            setWpProgression(12);
        }
    };

    // Funktion zum Generieren der Template-Email
    const generateTemplateEmail = (vorname, nachname) => {
        const cleanVorname = (vorname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
        const cleanNachname = (nachname || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
        if (cleanVorname && cleanNachname) {
            return `${cleanVorname}.${cleanNachname}@keinemail.de`;
        }
        return '';
    };

    // Absenden des kombinierten Formulars
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Prüfen ob Email leer ist für PV+WP Leads
        if (!formData.email && formData.name && formData.nachname) {
            // Email ist leer, Warnung anzeigen
            setPendingSubmit(e);
            setShowEmailWarningModal(true);
            return;
        }

        // Wenn Email leer ist aber keine Vor- und Nachnamen vorhanden, direkt absenden
        await submitLeadData();
    };

    // Eigentliche Submit-Funktion
    const submitLeadData = async (emailOverride = null) => {
        setLoading(true);

        try {
            // Zuerst PV-Lead erstellen
            const pvLeadData = {
                userId: user.id,
                ...formData,
                // Email-Override falls vorhanden (für Template-Email)
                email: emailOverride !== null ? emailOverride : formData.email,
                ...pvData,
                ...getPvConsumptionSubmitValues(pvData),
                produkt: 'PV',
                projekt: formData.projekt, // Explizit das Projekt-Feld übermitteln
                isQualified: isPvQualified(),
                pv_isQualified: isPvQualified(),
                customerStatus: 'Erfasst'
            };

            const pvResponse = await addLead(pvLeadData);
            
            if (!pvResponse.success) {
                throw new Error(pvResponse.error || 'Fehler beim Erstellen des PV-Leads');
            }

            // Dann WP-Lead erstellen (falls WP-Daten vorhanden sind)
            if (wpData.isOwner !== null) {
                const wpLeadData = {
                    userId: user.id,
                    ...formData,
                    // Email-Override falls vorhanden (für Template-Email)
                    email: emailOverride !== null ? emailOverride : formData.email,
                    // WP-Daten ohne Konflikte
                    isOwner: wpData.isOwner,
                    buildingType: wpData.buildingType,
                    livingAreaSize: wpData.livingAreaSize,
                    peopleInHouse: wpData.peopleInHouse,
                    buildYear: wpData.buildYear,
                    hasGasOrOil: wpData.hasGasOrOil,
                    heatingOlderThan2Years: wpData.heatingOlderThan2Years,
                    currentHeatingSystem: wpData.currentHeatingSystem,
                    hasSolarThermal: wpData.hasSolarThermal,
                    solarThermalRemoved: wpData.solarThermalRemoved,
                    hasMonumentProtection: wpData.hasMonumentProtection,
                    produkt: 'WP',
                    projekt: formData.projekt, // Explizit das Projekt-Feld übermitteln
                    isQualified: isWpQualified(),
                    wp_isQualified: isWpQualified(),
                    customerStatus: 'Erfasst'
                };

                const wpResponse = await addLead(wpLeadData);
                
                if (!wpResponse.success) {
                    throw new Error(wpResponse.error || 'Fehler beim Erstellen des WP-Leads');
                }
            }

            setSuccess('Leads erfolgreich hinzugefügt!');

            // Opt-In E-Mail senden, wenn E-Mail-Modus gewählt wurde
            if (optInMode === 'email' && formData.email) {
                try {
                    await sendEmail({
                        to: formData.email,
                        subject: 'Bestätigung Ihrer Anfrage',
                        message: 'Vielen Dank für Ihr Interesse an unseren Dienstleistungen.'
                    });
                } catch (emailError) {
                    console.error('E-Mail-Versand fehlgeschlagen:', emailError);
                }
            }

            setTimeout(() => {
                navigate('/leads');
            }, 2000);

        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Funktion zur Überprüfung der effektiven Dachfläche
    const getEffectiveRoofArea = () => {
        if (pvData.roofAreaOver20 === true && pvData.roofAreaSize) {
            return Number(pvData.roofAreaSize);
        }
        if (pvData.roofAreaOver20 === false &&
            pvData.hasAlternativeMounting === true &&
            pvData.alternativeMountingArea) {
            return Number(pvData.alternativeMountingArea);
        }
        return 0;
    };

    // Qualifizierungslogik für PV
    const isPvQualified = () => {
        const effectiveArea = getEffectiveRoofArea();
        return (
            pvData.isOwner === true &&
            pvData.buildingType !== '' &&
            pvData.roofOrientation !== 'Nord' &&
            effectiveArea >= 20 &&
            pvData.hasMonumentProtection === false &&
            hasQualifyingPvConsumption(pvData)
        );
    };

    // Qualifizierungslogik für WP
    const isWpQualified = () => {
        return (
            wpData.isOwner === true &&
            !isWpLivingAreaUnsupported() &&
            wpData.hasGasOrOil === true &&
            wpData.heatingOlderThan2Years === true &&
            wpData.hasMonumentProtection === false &&
            (wpData.hasSolarThermal === false ||
                (wpData.hasSolarThermal === true && wpData.solarThermalRemoved === true))
        );
    };

    // Rendering der verschiedenen Schritte
    const renderPvStep = () => {
        return (
            <PvFormStep
                pvData={pvData}
                pvProgression={pvProgression}
                onPvDataChange={handlePvDataChange}
                onPvOwnershipChange={handlePvOwnershipChange}
                onCancel={() => navigate('/leads')}
                onNext={nextStep}
                isPvStepValid={isPvStepValid}
                isPvQualified={isPvQualified}
                isPvDisqualified={isPvDisqualified}
            />
        );
    };

    const renderWpStep = () => {
        return (
            <WpFormStep
                wpData={wpData}
                wpProgression={wpProgression}
                onWpDataChange={handleWpDataChange}
                onWpOwnershipChange={handleWpOwnershipChange}
                onPrev={prevStep}
                onNext={nextStep}
                isWpStepValid={isWpStepValid}
                isWpQualified={isWpQualified}
                isWpDisqualified={isWpDisqualified}
            />
        );
    };

    const renderCustomerStep = () => {
        return (
            <div className="customer-form-step">
                <h2 className="form-title">👤 Kundendaten</h2>

                <div className="form-row">
                    <div className="form-field">
                        <label>Anrede*</label>
                        <select
                            name="anrede"
                            value={formData.anrede}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Bitte wählen</option>
                            <option value="herr">Herr</option>
                            <option value="frau">Frau</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Vorname*</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="form-field">
                        <label>Nachname*</label>
                        <input
                            type="text"
                            name="nachname"
                            value={formData.nachname}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>E-Mail</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-field">
                        <label>Handy*</label>
                        <input
                            type="tel"
                            name="handy"
                            value={formData.handy}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Straße*</label>
                        <input
                            type="text"
                            name="strasse"
                            value={formData.strasse}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="form-field">
                        <label>Hausnummer*</label>
                        <input
                            type="text"
                            name="hausnummer"
                            value={formData.hausnummer}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-field">
                        <label>Postleitzahl*</label>
                        <input
                            type="text"
                            name="postleitzahl"
                            value={formData.postleitzahl}
                            onChange={handleChange}
                            pattern="[0-9]{5}"
                            maxLength="5"
                            required
                        />
                    </div>
                    <div className="form-field">
                        <label>Stadt*</label>
                        <input
                            type="text"
                            name="stadt"
                            value={formData.stadt}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                {/* Datenschutztext */}
                <div className="form-section consent-section">
                    <div className="consent-text">
                        <p>Bestätigung der Einwilligung zur telefonischen oder persönlichen Kontaktaufnahme durch {getConsentBranding(user).companyName} oder deren Vertragspartner zur Datenerhebung, -verarbeitung und Informationszusendung. Die Datenschutzinformationen der {getConsentBranding(user).companyName} sind hier einzusehen: <a href={getConsentBranding(user).datenschutzUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d72b8', wordBreak: 'break-all' }}>{getConsentBranding(user).datenschutzUrl}</a> <span style={{ color: 'red' }}>*</span></p>
                    </div>
                </div>

                {/* Opt-In Bereich */}
                <OptInToggle
                    mode={optInMode}
                    onModeChange={setOptInMode}
                    signature={formData.signature}
                    onSignatureChange={handleOptInSignatureChange}
                    email={formData.email}
                    required={true}
                />

                <div className="form-actions">
                    <button type="button" className="prev-button" onClick={prevStep}>
                        ← Zurück zu WP
                    </button>
                    <button
                        type="submit"
                        className="submit-button"
                        disabled={loading || !isCustomerStepValid() || (optInMode === 'signature' && !formData.signature)}
                    >
                        {loading ? 'Wird gespeichert...' : 'Lead erstellen'}
                    </button>
                </div>
            </div>
        );
    };

    const handleLogout = () => {
        navigate('/');
    };

    const handleSectionChange = (section) => {
        if (section === 'dashboard') {
            navigate('/dashboard');
        } else if (section === 'termine') {
            navigate('/termine');
        } else if (section === 'stellungnahmen') {
            navigate('/stellungnahmen');
        } else if (section === 'leads') {
            navigate('/kunden');
        } else if (section === 'leads-overview') {
            navigate('/leads');
        }
    };

    return (
        <>
            <DashboardNav 
                activeSection="add-pv-wp-lead"
                onSectionChange={handleSectionChange}
                onLogout={handleLogout}
                user={user}
                onOpenOrganizationManagement={() => navigate('/organization-management')}
                onOpenMembersManagement={() => navigate('/organization-members')}
                onViewOrganizationLeads={() => navigate('/organization-leads')}
                onViewTeamLeads={() => {}}
                showOrgManagement={false}
                showMembersManagement={false}
                showOrganizationLeads={false}
            />
            <div className="page-container">
                <div className="combined-form">
            {/* Fortschrittsanzeige */}
            <div className="progress-indicator">
                <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-label">PV Anlage</span>
                </div>
                <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-label">Wärmepumpe</span>
                </div>
                <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
                    <span className="step-number">3</span>
                    <span className="step-label">Kundendaten</span>
                </div>
            </div>

            {/* Fehler- und Erfolgsmeldungen */}
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Formular-Schritte */}
            <form onSubmit={handleSubmit}>
                {step === 1 && renderPvStep()}
                {step === 2 && renderWpStep()}
                {step === 3 && renderCustomerStep()}
            </form>

            {/* CSS-Styles */}
            <style jsx>{`
        .combined-form {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .progress-indicator {
          display: flex;
          justify-content: center;
          margin-bottom: 30px;
          gap: 20px;
        }

        .progress-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          opacity: 0.5;
          transition: opacity 0.3s;
        }

        .progress-step.active {
          opacity: 1;
        }

        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: #4a6de5;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .step-label {
          font-size: 14px;
          font-weight: 500;
        }

        .form-title {
          text-align: center;
          margin-bottom: 30px;
          font-size: 24px;
          color: #333;
        }

        .form-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 30px;
          gap: 15px;
        }

        .form-actions button {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
        }

        .cancel-button {
          background-color: #6b7280;
          color: white;
        }

        .prev-button {
          background-color: #6b7280;
          color: white;
        }

        .next-button, .submit-button {
          background-color: #4a6de5;
          color: white;
        }

        .next-button:disabled, .submit-button:disabled {
          background-color: #d1d5db;
          cursor: not-allowed;
        }

        .error-message {
          background-color: #fee2e2;
          color: #dc2626;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .success-message {
          background-color: #dcfce7;
          color: #16a34a;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .consent-section {
          margin: 20px 0;
          padding: 15px;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #4a6de5;
        }

        .consent-text p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
        }

        .consent-text a {
          color: #1d72b8;
          text-decoration: underline;
          word-break: break-all;
        }

        .consent-text a:hover {
          color: #0f4c75;
        }
      `}</style>

            {/* Email-Warnung Modal */}
            {showEmailWarningModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '16px',
                    overflow: 'auto'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: window.innerWidth <= 768 ? '20px 16px' : '24px',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '100%',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <h3 style={{ 
                            marginTop: 0, 
                            marginBottom: '16px', 
                            color: '#d32f2f',
                            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                            lineHeight: '1.4'
                        }}>
                            ⚠️ E-Mail-Adresse ist leer
                        </h3>
                        <p style={{ 
                            marginBottom: '20px', 
                            color: '#666',
                            fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                            lineHeight: '1.5'
                        }}>
                            Die E-Mail-Adresse ist leer. Möchten Sie diese automatisch mit einem Template füllen?
                        </p>
                        <p style={{ 
                            marginBottom: '20px', 
                            padding: '12px', 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: '4px', 
                            fontFamily: 'monospace',
                            fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                            wordBreak: 'break-all',
                            overflowWrap: 'break-word'
                        }}>
                            {generateTemplateEmail(formData.name, formData.nachname)}
                        </p>
                        <div style={{ 
                            display: 'flex', 
                            gap: '12px', 
                            justifyContent: 'flex-end',
                            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowEmailWarningModal(false);
                                    setPendingSubmit(null);
                                }}
                                style={{
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                                    flex: window.innerWidth <= 768 ? '1' : 'none'
                                }}
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    // Email mit Template füllen
                                    const templateEmail = generateTemplateEmail(formData.name, formData.nachname);
                                    // Email im State aktualisieren
                                    setFormData(prev => ({ ...prev, email: templateEmail }));
                                    setShowEmailWarningModal(false);
                                    // Submit fortsetzen mit Template-Email
                                    await submitLeadData(templateEmail);
                                }}
                                style={{
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                                    flex: window.innerWidth <= 768 ? '1' : 'none'
                                }}
                            >
                                Ja, mit Template füllen
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    setShowEmailWarningModal(false);
                                    // Submit ohne Email fortsetzen
                                    await submitLeadData();
                                }}
                                style={{
                                    padding: window.innerWidth <= 768 ? '12px 16px' : '10px 20px',
                                    backgroundColor: '#d32f2f',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                                    flex: window.innerWidth <= 768 ? '1' : 'none'
                                }}
                            >
                                Nein, ohne E-Mail speichern
                            </button>
                        </div>
                    </div>
                </div>
            )}
                </div>
            </div>
        </>
    );
}

export default PvAndWPForm;